import { useState } from "react";
import PropTypes from "prop-types";
import { Button, Drawer, Typography } from "@mui/material";
import { GenericForm } from "common/Components/GenericForm";
import {
  PLATFORM_USER_ACCESS_LAYOUT,
  PLATFORM_USER_ACCESS_UI,
} from "pages/PlatformUserAccess/PlatformUserAccess.constants";
import { isInviteEmailFormatValid, useInviteEmailExists } from "pages/PlatformUserAccess/hooks/UseInviteEmailExists";
import {
  InviteDrawerContent,
  InviteDrawerFooter,
  InviteDrawerFormSection,
  InviteDrawerHeader,
  InviteDrawerScrollBody,
  drawerPaperSx,
} from "pages/PlatformUserAccess/components/InviteUserModal.styles";

const inviteFormFields = (roleOptions) => [
  {
    name: "email",
    label: "Email address",
    type: "text",
    required: true,
    placeholder: PLATFORM_USER_ACCESS_UI.inviteEmailPlaceholder,
  },
  {
    name: "roles",
    label: "Roles",
    type: "multiSelect",
    required: true,
    options: roleOptions,
  }
];

const normalizeRoleValues = (roles) =>
  (roles ?? []).map((role) =>
    typeof role === "string" ? role : role?.value || role?.label
  );

const CustomerInviteDrawer = ({
  open,
  onClose,
  roleOptions = [],
  onSubmit,
  checkInviteEmail,
}) => {
  const [formValues, setFormValues] = useState({
    email: "",
    roles: [],
  });
  const [formErrors, setFormErrors] = useState({});
  const roleSelectOptions = Array.isArray(roleOptions) ? roleOptions : [];

  const { checkEmailOnBlur } = useInviteEmailExists({ checkInviteEmail });

  const resetState = () => {
    setFormValues({ email: "", roles: []});
    setFormErrors({});
  };

  const handleCancel = () => {
    resetState();
    onClose();
  };

  const handleEmailBlur = async (_name, emailValue) => {
    const trimmed = String(emailValue ?? "").trim();
    if (trimmed && !isInviteEmailFormatValid(trimmed)) {
      setFormErrors((previous) => ({
        ...previous,
        email: PLATFORM_USER_ACCESS_UI.invalidEmailError,
      }));
      return;
    }
    const { exists, fieldError } = await checkEmailOnBlur(emailValue);
    if (fieldError) {
      setFormErrors((previous) => ({ ...previous, email: fieldError }));
      return;
    }
    setFormErrors((previous) => {
      if (
        !previous.email ||
        previous.email === PLATFORM_USER_ACCESS_UI.inviteEmailAlreadyExistsError ||
        previous.email === PLATFORM_USER_ACCESS_UI.inviteEmailValidationFailedError ||
        previous.email === PLATFORM_USER_ACCESS_UI.invalidEmailError
      ) {
        const next = { ...previous };
        delete next.email;
        return next;
      }
      return previous;
    });
  };

  const handleSubmit = async () => {
    const email = String(formValues.email ?? "").trim();
    const rolesNormalized = normalizeRoleValues(formValues.roles).filter(
      Boolean
    );

    const nextFormErrors = {};
    if (!isInviteEmailFormatValid(email)) {
      nextFormErrors.email = PLATFORM_USER_ACCESS_UI.invalidEmailError;
    }
    if (!rolesNormalized.length) {
      nextFormErrors.roles =
        PLATFORM_USER_ACCESS_UI.customerAccessRolesRequiredError;
    }
    if (
      formErrors.email === PLATFORM_USER_ACCESS_UI.inviteEmailAlreadyExistsError ||
      formErrors.email === PLATFORM_USER_ACCESS_UI.inviteEmailValidationFailedError
    ) {
      nextFormErrors.email = formErrors.email;
    }
    if (Object.keys(nextFormErrors).length > 0) {
      setFormErrors(nextFormErrors);
      return;
    }

    if (typeof checkInviteEmail === "function") {
      const { exists, error } = await checkInviteEmail(email);
      if (error) {
        setFormErrors({
          email: PLATFORM_USER_ACCESS_UI.inviteEmailValidationFailedError,
        });
        return;
      }
      if (exists) {
        setFormErrors({
          email: PLATFORM_USER_ACCESS_UI.inviteEmailAlreadyExistsError,
        });
        return;
      }
    }

    setFormErrors({});
    const success = await onSubmit({
      email,
      roles: rolesNormalized,
      sendInviteEmail: true,
    });
    if (success) {
      resetState();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleCancel}
      PaperProps={{ sx: drawerPaperSx }}
    >
      <InviteDrawerContent
        $drawerWidth={PLATFORM_USER_ACCESS_LAYOUT.drawerWidth}
      >
        <InviteDrawerHeader>
          <Typography variant="h6">
            {PLATFORM_USER_ACCESS_UI.inviteUserLabel}
          </Typography>
        </InviteDrawerHeader>
        <InviteDrawerScrollBody>
          <InviteDrawerFormSection>
            <GenericForm
              fields={inviteFormFields(roleSelectOptions)}
              values={formValues}
              errors={formErrors}
              onChange={(name, value) => {
                setFormValues((previous) => ({ ...previous, [name]: value }));
                if (name === "email") {
                  setFormErrors((previous) => {
                    if (!previous.email) {
                      return previous;
                    }
                    const next = { ...previous };
                    delete next.email;
                    return next;
                  });
                }
                if (name === "roles") {
                  setFormErrors((previous) => {
                    if (!previous.roles) {
                      return previous;
                    }
                    const next = { ...previous };
                    delete next.roles;
                    return next;
                  });
                }
              }}
              onFieldBlur={(name, value) => {
                if (name === "email") {
                  handleEmailBlur(name, value);
                }
              }}
              showBorder={false}
            />
          </InviteDrawerFormSection>
        </InviteDrawerScrollBody>
        <InviteDrawerFooter>
          <Button onClick={handleCancel}>
            {PLATFORM_USER_ACCESS_UI.cancelLabel}
          </Button>
          <Button onClick={handleSubmit} variant="contained">
            {PLATFORM_USER_ACCESS_UI.sendInviteLabel}
          </Button>
        </InviteDrawerFooter>
      </InviteDrawerContent>
    </Drawer>
  );
};

CustomerInviteDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  roleOptions: PropTypes.arrayOf(PropTypes.object),
  onSubmit: PropTypes.func.isRequired,
  checkInviteEmail: PropTypes.func,
};

export default CustomerInviteDrawer;
